import React, { useState } from "react";
import { Camera, ChevronRight, Image } from "lucide-react";
import theme from "../theme";
import PhotoViewer from "./Gallery";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

const ServiceCard = ({
  icon: Icon,
  title,
  description,
  image,
  imgs = [], // Gallery images
  isSmall = false,
  badge,
}) => {
  const { t } = useTranslation();
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);

  const handleSeePicturesClick = (e) => {
    e.stopPropagation();
    setIsGalleryOpen(true);
    //disable body scroll when gallery is open
    document.body.style.overflow = "hidden";
  };

  //close gallery and enable body scroll
  const handleCloseGallery = () => {
    setIsGalleryOpen(false);
    document.body.style.overflow = "auto";
  };

  if (isSmall) {
    return (
      <>
        <div
          className="relative rounded-2xl overflow-hidden bg-white shadow-sm"
          style={{ borderBottom: `4px solid ${theme.wheat}` }}
        >
          <div className="relative p-4 sm:p-5 text-center">
            <div
              className="w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center mx-auto mb-3"
              style={{ backgroundColor: theme.blush }}
            >
              <Icon size={24} style={{ color: theme.warmBrown }} />
            </div>
            <h3
              className="text-base sm:text-lg font-bold mb-2"
              style={{ color: theme.strongerPink }}
            >
              {title}
            </h3>
            <p className="text-sm" style={{ color: theme.warmBrown }}>
              {description}
            </p>
            {badge && (
              <div
                className="inline-flex items-center gap-2 px-3 py-2 rounded-full mt-3"
                style={{ backgroundColor: theme.warmBrown + "11" }}
              >
                <span className="text-sm" style={{ color: theme.warmBrown }}>
                  {badge}
                </span>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div
        className="relative rounded-none sm:rounded-3xl overflow-hidden shadow-sm sm:min-h-80"
        style={{
          borderBottom: `4px solid ${theme.wheat}`,
          backgroundImage: `url(${image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* Gradient overlay for text readability - dark bottom to transparent top */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 via-black/10 rounded-none sm:rounded-3xl"></div>

        {/* See Pictures Button Overlay */}
        <div className="absolute top-4 right-4 z-20 flex">
          {badge && (
            <div
                className="inline-flex items-center gap-2 px-3 py-2 rounded-full mb-4 backdrop-blur-md"
                style={{ backgroundColor: theme.warmBrown + "77" }}
              >
                <span className="text-sm" style={{ color: theme.cream }}>
                  {badge}
                </span>
              </div>
          )}
          <div 
            onClick={handleSeePicturesClick} className="flex items-center px-3 py-1.5 rounded-full mb-4 ml-3 bg-white cursor-pointer">
              <Image size={18} className="mr-2" />
              <span className="text-sm">{t("btnSeePhotos")}</span>
            </div>
        </div>

        {/* Content overlaid on background */}
        <div className="relative sm:absolute z-10 p-6 h-full flex flex-col justify-end">
          <div className="h-[5rem] w-full bg-transparent">

          </div>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 bg-white/30 backdrop-blur-md border border-white/20">
              <Icon size={24} className="text-white" />
            </div>
            <h3 className="text-2xl font-bold text-white drop-shadow-md">
              {title}
            </h3>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isGalleryOpen && (
          <PhotoViewer
            imgs={imgs}
            onClose={handleCloseGallery}
            initialIndex={0}
            className="z-[999999]"
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default ServiceCard;
